import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
// import showToast from '../../utils/toastHelper';

const Results = () => {
  const [elections, setElections] = useState([]);
  const [selectedElection, setSelectedElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [groupData, setGroupData] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [organization, setOrganization] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [groupTypeFilter, setGroupTypeFilter] = useState('all'); // 'all' | 'class' | 'department'

  const selectedElectionRef = React.useRef(null);

  useEffect(() => {
    fetchOrganization();
    fetchElections(true);
    
    // Auto-refresh every 15 seconds — silent background update
    const interval = setInterval(() => {
      fetchElections(false);
    }, 15000);
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrganization = async () => {
    try {
      const token = localStorage.getItem('token');
      const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (userRes.data.data?.organizationId) {
        const orgRes = await axios.get(`${process.env.REACT_APP_API_URL}/organizations/${userRes.data.data.organizationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrganization(orgRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    }
  };

  const fetchElections = async (isInitial = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const electionsData = response.data.data || [];
      setElections(electionsData);

      // Auto-select only if there's exactly one election
      if (isInitial && electionsData.length === 1 && !selectedElectionRef.current) {
        selectedElectionRef.current = electionsData[0].id;
        setSelectedElection(electionsData[0]);
      } else if (selectedElectionRef.current) {
        // Silently update selected election data without resetting the dropdown
        const updated = electionsData.find(e => e.id === selectedElectionRef.current);
        if (updated) setSelectedElection(prev => ({ ...prev, ...updated }));
      }
    } catch (error) {
      console.error('Failed to fetch elections:', error);
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  const fetchGroupData = async (electionId) => {
    setLoadingGroups(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections/${electionId}/results-by-group`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroupData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch group data:', error);
      setGroupData(null);
    } finally {
      setLoadingGroups(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: '#6b7280',
      active: '#10b981',
      completed: '#3b82f6',
      cancelled: '#ef4444',
    };
    return colors[status] || '#6b7280';
  };

  const getTotalVotes = (election) => {
    let total = 0;
    election.positions?.forEach(position => {
      position.candidates?.forEach(candidate => {
        total += parseInt(candidate.voteCount) || 0;
      });
    });
    return total;
  };

  const getWinner = (candidates) => {
    if (!candidates || candidates.length === 0) return null;
    return candidates.reduce((prev, current) => 
      (current.voteCount || 0) > (prev.voteCount || 0) ? current : prev
    );
  };

  const calculatePercentage = (voteCount, totalVotes) => {
    if (totalVotes === 0) return 0;
    return ((voteCount / totalVotes) * 100).toFixed(1);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Filter group data by selected group type
  const getFilteredGroupData = () => {
    if (!groupData) return null;
    if (groupTypeFilter === 'all') return groupData;

    const filteredParticipation = groupData.participation?.filter(
      g => g.groupType === groupTypeFilter || g.groupId === null
    ) || [];
    const allowedGroupIds = new Set(filteredParticipation.map(g => g.groupId));

    const filteredBreakdown = groupData.breakdown?.map(position => ({
      ...position,
      candidates: position.candidates?.map(candidate => ({
        ...candidate,
        byGroup: candidate.byGroup?.filter(g => allowedGroupIds.has(g.groupId)),
      })),
    })) || [];

    return { ...groupData, participation: filteredParticipation, breakdown: filteredBreakdown };
  };

  // Shared style constants for Excel exports
  const PURPLE_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667EEA' } };
  const DARK_PURPLE_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4C51BF' } };
  const GREEN_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF10B981' } };
  const GOLD_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
  const WINNER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } };
  const HEADER_BG = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
  const THIN_BORDER = {
    top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
  };

  const applyBordersToRow = (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => { cell.border = THIN_BORDER; });
  };

  // Build an Overview sheet on the given workbook
  const buildOverviewSheet = (wb) => {
    if (!selectedElection) return;
    const positions = selectedElection.positions?.filter(p => p.candidates && p.candidates.length > 0) || [];
    if (positions.length === 0) return;

    const ws = wb.addWorksheet('Results Overview');
    ws.columns = [
      { width: 10 },  // Rank
      { width: 28 }, // Candidate
      { width: 12 }, // Votes
      { width: 14 }, // Percentage
      { width: 18 }, // Status
    ];

    // Election title row
    const titleRow = ws.addRow([selectedElection.name]);
    titleRow.font = { size: 16, bold: true, color: { argb: 'FF2C3E50' } };
    ws.mergeCells(titleRow.number, 1, titleRow.number, 5);

    // Status + total votes row
    const infoRow = ws.addRow([`Status: ${selectedElection.status}`, '', '', '', `Total Votes: ${getTotalVotes(selectedElection)}`]);
    infoRow.font = { size: 11, color: { argb: 'FF6B7280' } };
    infoRow.getCell(5).font = { size: 11, bold: true, color: { argb: 'FF2C3E50' } };

    ws.addRow([]); // spacer

    positions.forEach((position) => {
      const sorted = [...(position.candidates || [])].sort(
        (a, b) => (parseInt(b.voteCount) || 0) - (parseInt(a.voteCount) || 0)
      );
      const totalVotes = sorted.reduce((sum, c) => sum + (parseInt(c.voteCount) || 0), 0);
      const winner = sorted.length > 0 && totalVotes > 0 ? sorted[0] : null;

      // Position header row — purple background, white text
      const posRow = ws.addRow([position.name, '', '', '', `Total Votes: ${totalVotes}`]);
      ws.mergeCells(posRow.number, 1, posRow.number, 4);
      posRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = PURPLE_FILL;
        cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { vertical: 'middle' };
      });
      posRow.height = 28;

      // Description row (if exists)
      if (position.description) {
        const descRow = ws.addRow([position.description]);
        ws.mergeCells(descRow.number, 1, descRow.number, 5);
        descRow.font = { italic: true, size: 10, color: { argb: 'FF9CA3AF' } };
      }

      // Column headers row
      const headerRow = ws.addRow(['Rank', 'Candidate', 'Votes', 'Percentage', 'Status']);
      headerRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = HEADER_BG;
        cell.font = { bold: true, size: 10, color: { argb: 'FF374151' } };
        cell.border = THIN_BORDER;
        cell.alignment = { vertical: 'middle' };
      });

      // Candidate data rows
      sorted.forEach((c, idx) => {
        const votes = parseInt(c.voteCount) || 0;
        const pct = totalVotes > 0 ? (votes / totalVotes * 100).toFixed(1) : '0.0';
        const isWinner = winner && c.id === winner.id;

        const dataRow = ws.addRow([
          `#${idx + 1}`,
          c.user?.fullName || 'Unknown',
          votes,
          `${pct}%`,
          isWinner && totalVotes > 0 ? '✅ WINNER' : '',
        ]);
        applyBordersToRow(dataRow);

        if (isWinner && totalVotes > 0) {
          // Gold highlight for winner row
          dataRow.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = GOLD_FILL;
            cell.font = { bold: true, size: 10, color: { argb: 'FF92400E' } };
          });
          dataRow.getCell(1).value = `🏆 #1`;
          // Green "WINNER" badge
          dataRow.getCell(5).fill = WINNER_FILL;
          dataRow.getCell(5).font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
          dataRow.getCell(5).alignment = { horizontal: 'center', vertical: 'middle' };
        }

        dataRow.getCell(3).alignment = { horizontal: 'center' };
        dataRow.getCell(4).alignment = { horizontal: 'center' };
      });

      ws.addRow([]); // spacer between positions
    });
  };

  // Get smart label based on active filter
  const getGroupLabel = () => {
    if (groupTypeFilter === 'class') return 'Class';
    if (groupTypeFilter === 'department') return 'Department';
    return 'Group';
  };

  // Build a By Group sheet on the given workbook
  const buildGroupSheet = (wb) => {
    const filtered = getFilteredGroupData();
    if (!filtered || !selectedElection) return;
    const label = getGroupLabel();

    const ws = wb.addWorksheet(`Results By ${label}`);

    // Election title
    const titleRow = ws.addRow([`${selectedElection.name} — Results By ${label}`]);
    titleRow.font = { size: 16, bold: true, color: { argb: 'FF2C3E50' } };
    ws.mergeCells(titleRow.number, 1, titleRow.number, 6);

    ws.addRow([]); // spacer

    // === Participation section ===
    if (filtered.participation && filtered.participation.length > 0) {
      const sectionRow = ws.addRow([`Participation by ${label}`]);
      // Show Type column only when filter is 'all' (mixed types)
      const showTypeCol = groupTypeFilter === 'all';
      const partColCount = showTypeCol ? 5 : 4;
      ws.mergeCells(sectionRow.number, 1, sectionRow.number, partColCount);
      sectionRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = GREEN_FILL;
        cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { vertical: 'middle' };
      });
      sectionRow.height = 28;

      const partHeaders = [label];
      if (showTypeCol) partHeaders.push('Type');
      partHeaders.push('Members', 'Voted', 'Turnout (%)');
      const partHeaderRow = ws.addRow(partHeaders);
      partHeaderRow.eachCell((cell) => {
        cell.fill = HEADER_BG;
        cell.font = { bold: true, size: 10, color: { argb: 'FF374151' } };
        cell.border = THIN_BORDER;
      });

      filtered.participation.forEach(g => {
        const row = [g.groupName];
        if (showTypeCol) row.push(g.groupType || 'N/A');
        row.push(g.totalMembers, g.totalVoted, `${g.percentage}%`);
        const dataRow = ws.addRow(row);
        applyBordersToRow(dataRow);
        const turnoutCell = dataRow.getCell(partHeaders.length);
        const pctVal = parseFloat(g.percentage);
        if (pctVal >= 50) {
          turnoutCell.font = { bold: true, color: { argb: 'FF059669' } };
        } else if (pctVal > 0) {
          turnoutCell.font = { color: { argb: 'FFD97706' } };
        }
      });

      ws.addRow([]); // spacer
    }

    // === Vote breakdown per position ===
    filtered.breakdown?.forEach((position) => {
      if (!position.candidates || position.candidates.length === 0) return;
      const groupNames = position.candidates[0]?.byGroup?.map(g => g.groupName) || [];
      const totalCols = 2 + groupNames.length;

      // Position header + winner label
      const sortedCands = [...position.candidates].sort((a, b) => b.totalVotes - a.totalVotes);
      const topCandidate = sortedCands.length > 0 && sortedCands[0].totalVotes > 0 ? sortedCands[0] : null;
      const posLabel = topCandidate
        ? `${position.positionName}    —    Winner: ${topCandidate.candidateName}`
        : position.positionName;

      const posRow = ws.addRow([posLabel]);
      ws.mergeCells(posRow.number, 1, posRow.number, Math.min(totalCols, 20));
      posRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = DARK_PURPLE_FILL;
        cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { vertical: 'middle' };
      });
      posRow.height = 26;

      const colHeaders = ['Candidate', 'Total', ...groupNames];
      const colHeaderRow = ws.addRow(colHeaders);
      colHeaderRow.eachCell((cell, colNumber) => {
        cell.fill = HEADER_BG;
        cell.font = { bold: true, size: 9, color: { argb: 'FF374151' } };
        cell.border = THIN_BORDER;
        if (colNumber >= 2) cell.alignment = { horizontal: 'center' };
      });

      sortedCands.forEach((candidate, idx) => {
        const rowData = [candidate.candidateName, candidate.totalVotes];
        candidate.byGroup?.forEach(g => rowData.push(g.voteCount));
        const dataRow = ws.addRow(rowData);
        applyBordersToRow(dataRow);

        if (idx === 0 && candidate.totalVotes > 0) {
          dataRow.getCell(1).font = { bold: true };
          dataRow.getCell(1).value = `🏆 ${candidate.candidateName}`;
          dataRow.getCell(2).font = { bold: true, color: { argb: 'FF059669' } };
        }

        dataRow.eachCell((cell, colNumber) => {
          if (colNumber >= 2) {
            cell.alignment = { horizontal: 'center' };
            if (cell.value === 0) {
              cell.font = { color: { argb: 'FFD1D5DB' } };
            }
          }
        });
      });

      ws.addRow([]); // spacer
    });

    ws.getColumn(1).width = 22;
    ws.getColumn(2).width = 10;
  };

  // Main export handler — called from the modal
  const handleExport = async (type) => {
    setIsExporting(true);
    try {
      const wb = new ExcelJS.Workbook();

      if (type === 'overview' || type === 'both') {
        buildOverviewSheet(wb);
      }
      if (type === 'groups' || type === 'both') {
        // Ensure group data is loaded before exporting
        if (!groupData && selectedElection) {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections/${selectedElection.id}/results-by-group`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setGroupData(response.data.data);
        }
        // buildGroupSheet reads from getFilteredGroupData() which uses groupData state
        // Since setState is async, call it after a tick to ensure state is settled
        await new Promise(r => setTimeout(r, 50));
        buildGroupSheet(wb);
      }

      const label = getGroupLabel();
      const suffix = type === 'both' ? 'Full Results' : type === 'overview' ? 'Results Overview' : `Results By ${label}`;
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${selectedElection.name} - ${suffix}.xlsx`);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
      setShowExportModal(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (elections.length === 0) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <i className="bi bi-bar-chart" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
          <p className="mt-3 text-muted">No elections found</p>
          <Link to="/admin/elections" className="btn btn-primary mt-3">
            Create Election
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="mb-4">
        <h3 className="mb-1" style={{ fontWeight: '600' }}>Results & Analytics</h3>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          View voting results and statistics
        </p>
      </div>

      {/* Election selector: list if multiple, auto-select if one */}
      {!selectedElection ? (
        <div className="card" style={{ border: 'none' }}>
          <div className="card-header bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>Select an Election</h5>
          </div>
          <div className="card-body p-0">
            {elections.map((election, idx) => {
              const totalVotes = getTotalVotes(election);
              return (
                <div
                  key={election.id}
                  className="d-flex justify-content-between align-items-center px-4 py-3"
                  onClick={() => {
                    selectedElectionRef.current = election.id;
                    setSelectedElection(election);
                  }}
                  style={{
                    cursor: 'pointer',
                    borderBottom: idx < elections.length - 1 ? '1px solid #f3f4f6' : 'none',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div className="d-flex align-items-center">
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '10px',
                      background: `${getStatusColor(election.status)}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '1rem', flexShrink: 0,
                    }}>
                      <i className="bi bi-calendar-event" style={{ color: getStatusColor(election.status), fontSize: '1.1rem' }}></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{election.name}</div>
                      {election.description && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          {election.description.substring(0, 60)}{election.description.length > 60 && '...'}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <span className="badge" style={{ backgroundColor: getStatusColor(election.status), textTransform: 'capitalize', fontSize: '0.7rem' }}>
                      {election.status}
                    </span>
                    <div className="text-end" style={{ minWidth: '50px' }}>
                      <div style={{ fontSize: '1rem', fontWeight: '700', color: '#2c3e50' }}>{formatNumber(parseInt(totalVotes) || 0)}</div>
                      <div style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>votes</div>
                    </div>
                    <i className="bi bi-chevron-right" style={{ color: '#d1d5db', fontSize: '0.9rem' }}></i>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          {/* Election header card */}
          <div className="card mb-4" style={{ border: 'none' }}>
            <div className="card-body py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  {elections.length > 1 && (
                    <button
                      className="btn btn-sm btn-light me-3"
                      onClick={() => { setSelectedElection(null); selectedElectionRef.current = null; setGroupData(null); setActiveTab('overview'); }}
                      style={{ borderRadius: '8px' }}
                    >
                      <i className="bi bi-arrow-left"></i>
                    </button>
                  )}
                  <div>
                    <h5 className="mb-0" style={{ fontWeight: '700', fontSize: '1.1rem' }}>{selectedElection.name}</h5>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <span className="badge" style={{ backgroundColor: getStatusColor(selectedElection.status), textTransform: 'capitalize', fontSize: '0.65rem' }}>
                        {selectedElection.status}
                      </span>
                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>{formatNumber(parseInt(getTotalVotes(selectedElection)) || 0)} total votes</small>
                    </div>
                  </div>
                </div>
                {/* Export button */}
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => setShowExportModal(true)}
                    style={{ fontSize: '0.8rem', fontWeight: '500' }}
                  >
                    <i className="bi bi-file-earmark-excel me-1"></i>Export
                  </button>
                {/* Tabs inline */}
                <div className="d-flex" style={{ background: '#f3f4f6', borderRadius: '8px', padding: '3px' }}>
                  <button
                    className={`btn btn-sm ${activeTab === 'overview' ? '' : ''}`}
                    onClick={() => setActiveTab('overview')}
                    style={{
                      background: activeTab === 'overview' ? 'white' : 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: activeTab === 'overview' ? '600' : '400',
                      color: activeTab === 'overview' ? '#2c3e50' : '#6b7280',
                      padding: '0.35rem 1rem',
                      boxShadow: activeTab === 'overview' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    <i className="bi bi-bar-chart me-1"></i>Overview
                  </button>
                  <button
                    className={`btn btn-sm`}
                    onClick={() => { setActiveTab('groups'); if (!groupData && selectedElection) fetchGroupData(selectedElection.id); }}
                    style={{
                      background: activeTab === 'groups' ? 'white' : 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: activeTab === 'groups' ? '600' : '400',
                      color: activeTab === 'groups' ? '#2c3e50' : '#6b7280',
                      padding: '0.35rem 1rem',
                      boxShadow: activeTab === 'groups' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    <i className="bi bi-collection me-1"></i>By Group
                  </button>
                </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Group Breakdown Tab */}
      {selectedElection && activeTab === 'groups' && (
        (() => {
          const filtered = getFilteredGroupData();
          return (
          <div>
            {loadingGroups ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
            ) : filtered ? (
              <>
                {/* Participation by Group */}
                <div className="card mb-4">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '2px solid #10b981' }}>
                    <h5 className="mb-0" style={{ fontWeight: '600' }}><i className="bi bi-people me-2" style={{ color: '#10b981' }}></i>Participation by Group</h5>
                    {/* Group type filter */}
                    <div className="d-flex" style={{ background: '#f3f4f6', borderRadius: '6px', padding: '2px' }}>
                      {[{ key: 'all', label: 'All' }, { key: 'class', label: 'Classes' }, { key: 'department', label: 'Departments' }].map(opt => (
                        <button
                          key={opt.key}
                          className="btn btn-sm"
                          onClick={() => setGroupTypeFilter(opt.key)}
                          style={{
                            background: groupTypeFilter === opt.key ? 'white' : 'transparent',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '0.75rem',
                            fontWeight: groupTypeFilter === opt.key ? '600' : '400',
                            color: groupTypeFilter === opt.key ? '#2c3e50' : '#6b7280',
                            padding: '0.25rem 0.75rem',
                            boxShadow: groupTypeFilter === opt.key ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="card-body">
                    {filtered.participation && filtered.participation.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover align-middle">
                          <thead>
                            <tr>
                              <th>Group</th>
                              <th>Type</th>
                              <th>Members</th>
                              <th>Voted</th>
                              <th>Turnout</th>
                              <th style={{ width: '30%' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.participation.map((g, i) => (
                              <tr key={i}>
                                <td style={{ fontWeight: '500' }}>{g.groupName}</td>
                                <td><span className={`badge ${g.groupType === 'class' ? 'bg-info' : g.groupType === 'department' ? 'bg-warning' : 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>{g.groupType || 'N/A'}</span></td>
                                <td>{g.totalMembers}</td>
                                <td><strong>{g.totalVoted}</strong></td>
                                <td><span className="badge bg-success" style={{ fontSize: '0.8rem' }}>{g.percentage}%</span></td>
                                <td>
                                  <div className="progress" style={{ height: '8px' }}>
                                    <div className="progress-bar" style={{ width: `${g.percentage}%`, background: 'linear-gradient(90deg, #10b981, #059669)' }}></div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4">No group data available. Add voters to groups to see participation breakdown.</p>
                    )}
                  </div>
                </div>

                {/* Vote Breakdown by Group per Position */}
                {filtered.breakdown && filtered.breakdown.map(position => (
                  <div key={position.positionId} className="card mb-4">
                    <div className="card-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '1rem' }}>
                      <h6 className="mb-0" style={{ fontWeight: '600' }}>{position.positionName}</h6>
                    </div>
                    <div className="card-body">
                      {position.candidates && position.candidates.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm align-middle">
                            <thead>
                              <tr>
                                <th>Candidate</th>
                                <th>Total</th>
                                {position.candidates[0]?.byGroup?.map(g => (
                                  <th key={g.groupId} style={{ fontSize: '0.75rem', textAlign: 'center' }}>{g.groupName}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {position.candidates.sort((a, b) => b.totalVotes - a.totalVotes).map(candidate => (
                                <tr key={candidate.candidateId}>
                                  <td style={{ fontWeight: '500' }}>{candidate.candidateName}</td>
                                  <td><strong>{candidate.totalVotes}</strong></td>
                                  {candidate.byGroup?.map(g => (
                                    <td key={g.groupId} style={{ textAlign: 'center' }}>
                                      <span style={{ fontWeight: g.voteCount > 0 ? '600' : '400', color: g.voteCount > 0 ? '#2c3e50' : '#d1d5db' }}>
                                        {g.voteCount}
                                      </span>
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted text-center py-3">No candidates</p>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="card"><div className="card-body text-center py-5 text-muted">No group data available</div></div>
            )}
          </div>
          );
        })()
      )}

      {/* Results Overview Tab */}
      {selectedElection && activeTab === 'overview' && (
        <div className="row">
          {selectedElection.positions && selectedElection.positions.filter(p => p.candidates && p.candidates.length > 0).length > 0 ? (
            selectedElection.positions.filter(p => p.candidates && p.candidates.length > 0).map((position) => {
              const sortedCandidates = [...(position.candidates || [])].sort(
                (a, b) => (parseInt(b.voteCount) || 0) - (parseInt(a.voteCount) || 0)
              );
              const totalVotes = sortedCandidates.reduce((sum, c) => sum + (parseInt(c.voteCount) || 0), 0);
              const winner = getWinner(sortedCandidates);

              return (
                <div key={position.id} className="col-12 mb-4">
                  <div className="card">
                    <div
                      className="card-header"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '1.25rem'
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0" style={{ fontWeight: '700' }}>{position.name}</h5>
                          {position.description && (
                            <small style={{ opacity: 0.9 }}>{position.description}</small>
                          )}
                        </div>
                        <div className="text-end">
                          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Votes</div>
                          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{formatNumber(parseInt(totalVotes) || 0)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body p-4">
                      {sortedCandidates.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <i className="bi bi-person-x" style={{ fontSize: '2rem' }}></i>
                          <p className="mt-2 mb-0">No candidates</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th style={{ width: '50px' }}>Rank</th>
                                <th>Candidate</th>
                                <th style={{ width: '150px' }}>Votes</th>
                                <th style={{ width: '100px' }}>Percentage</th>
                                <th style={{ width: '40%' }}>Distribution</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedCandidates.map((candidate, index) => {
                                const percentage = calculatePercentage(candidate.voteCount || 0, totalVotes);
                                const isWinner = winner && candidate.id === winner.id && totalVotes > 0;

                                return (
                                  <tr key={candidate.id} style={{ backgroundColor: isWinner ? '#f0fdf4' : 'transparent' }}>
                                    <td>
                                      <div className="d-flex align-items-center" style={{ minWidth: '60px' }}>
                                        {isWinner ? (
                                          <i className="bi bi-trophy-fill me-2" style={{ color: '#fbbf24', fontSize: '1.25rem' }}></i>
                                        ) : (
                                          <span style={{ width: '28px', display: 'inline-block' }}></span>
                                        )}
                                        <span style={{ fontWeight: isWinner ? '700' : '500' }}>#{index + 1}</span>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        {candidate.photoUrl ? (
                                          <img
                                            src={candidate.photoUrl}
                                            alt={candidate.user?.fullName}
                                            style={{
                                              width: '40px',
                                              height: '40px',
                                              borderRadius: '50%',
                                              objectFit: 'cover',
                                              marginRight: '0.75rem'
                                            }}
                                          />
                                        ) : (
                                          <div
                                            style={{
                                              width: '40px',
                                              height: '40px',
                                              borderRadius: '50%',
                                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              color: 'white',
                                              fontSize: '1rem',
                                              fontWeight: '600',
                                              marginRight: '0.75rem'
                                            }}
                                          >
                                            {candidate.user?.fullName?.charAt(0) || '?'}
                                          </div>
                                        )}
                                        <div>
                                          <div style={{ fontWeight: '600' }}>{candidate.user?.fullName || 'Unknown'}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <strong style={{ fontSize: '1.125rem' }}>{candidate.voteCount || 0}</strong>
                                    </td>
                                    <td>
                                      <span className="badge bg-secondary" style={{ fontSize: '0.875rem' }}>
                                        {percentage}%
                                      </span>
                                    </td>
                                    <td>
                                      <div className="progress" style={{ height: '24px' }}>
                                        <div
                                          className="progress-bar"
                                          role="progressbar"
                                          style={{
                                            width: `${percentage}%`,
                                            background: isWinner ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            fontSize: '0.813rem',
                                            fontWeight: '600'
                                          }}
                                          aria-valuenow={percentage}
                                          aria-valuemin="0"
                                          aria-valuemax="100"
                                        >
                                          {percentage > 5 && `${percentage}%`}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="bi bi-trophy text-muted" style={{ fontSize: '3rem' }}></i>
                  <p className="mt-3 text-muted">No positions in this election</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Export Confirmation Modal */}
      {showExportModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px' }}>
            <div className="modal-content" style={{ borderRadius: '12px', border: 'none' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #f3f4f6', padding: '1.25rem 1.5rem' }}>
                <h5 className="modal-title" style={{ fontWeight: '700', fontSize: '1.05rem' }}>
                  <i className="bi bi-file-earmark-excel me-2" style={{ color: '#10b981' }}></i>
                  Export Results
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowExportModal(false)} disabled={isExporting}></button>
              </div>
              <div className="modal-body" style={{ padding: '1.5rem' }}>
                <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                  Choose what to include in the Excel export for <strong>{selectedElection?.name}</strong>:
                </p>
                <div className="d-flex flex-column gap-2">
                  <button
                    className="btn btn-outline-primary d-flex align-items-center justify-content-between py-2 px-3"
                    onClick={() => handleExport('overview')}
                    disabled={isExporting}
                    style={{ borderRadius: '8px', fontSize: '0.875rem' }}
                  >
                    <span><i className="bi bi-bar-chart me-2"></i>Overview Only</span>
                    <small className="text-muted">Positions, candidates, votes</small>
                  </button>
                  <button
                    className="btn btn-outline-primary d-flex align-items-center justify-content-between py-2 px-3"
                    onClick={() => handleExport('groups')}
                    disabled={isExporting}
                    style={{ borderRadius: '8px', fontSize: '0.875rem' }}
                  >
                    <span><i className="bi bi-collection me-2"></i>By Group Only</span>
                    <small className="text-muted">Participation & group breakdown</small>
                  </button>
                  <button
                    className="btn btn-primary d-flex align-items-center justify-content-between py-2 px-3"
                    onClick={() => handleExport('both')}
                    disabled={isExporting}
                    style={{ borderRadius: '8px', fontSize: '0.875rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                  >
                    <span><i className="bi bi-file-earmark-spreadsheet me-2"></i>Both (Recommended)</span>
                    <small style={{ opacity: 0.85 }}>Single file, 2 sheets</small>
                  </button>
                </div>
                {isExporting && (
                  <div className="text-center mt-3">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    <small className="text-muted">Generating export...</small>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #f3f4f6', padding: '0.75rem 1.5rem' }}>
                <button className="btn btn-sm btn-light" onClick={() => setShowExportModal(false)} disabled={isExporting}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Results;
